import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon, EyeIcon, EditIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { Customer } from '../types/Customer';
export function Customers() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // Mock data - in a real app, this would come from an API
  const customers: Customer[] = [{
    id: '1',
    name: 'Acme Corporation',
    email: 'billing@acme.com',
    phone: '+94 77 123 4567',
    address: '123 Business Street',
    city: 'Colombo',
    country: 'Sri Lanka',
    createdAt: '2023-06-15',
    totalSpent: 875000,
    invoiceCount: 12,
    status: 'active'
  }, {
    id: '2',
    name: 'Tech Solutions Ltd',
    email: 'accounts@techsolutions.lk',
    phone: '+94 11 234 5678',
    address: '456 Tech Park',
    city: 'Kandy',
    country: 'Sri Lanka',
    createdAt: '2023-08-20',
    totalSpent: 450000,
    invoiceCount: 8,
    status: 'active'
  }, {
    id: '3',
    name: 'Global Enterprises',
    email: 'finance@global.com',
    phone: '+94 71 345 6789',
    address: '789 Corporate Avenue',
    city: 'Galle',
    country: 'Sri Lanka',
    createdAt: '2023-04-10',
    totalSpent: 1250000,
    invoiceCount: 15,
    status: 'active'
  }, {
    id: '4',
    name: 'Innovation Hub',
    email: 'billing@innovationhub.lk',
    phone: '+94 77 456 7890',
    address: '321 Innovation Drive',
    city: 'Colombo',
    country: 'Sri Lanka',
    createdAt: '2023-09-05',
    totalSpent: 180000,
    invoiceCount: 4,
    status: 'inactive'
  }];
  const getStatusBadge = (status: Customer['status']) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-4000">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) || customer.email.toLowerCase().includes(searchTerm.toLowerCase()) || customer.phone && customer.phone.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || customer.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const totalCustomers = filteredCustomers.length;
  const activeCustomers = filteredCustomers.filter(c => c.status === 'active').length;
  const totalRevenue = filteredCustomers.reduce((sum, c) => sum + c.totalSpent, 0);
  const avgRevenue = totalCustomers > 0 ? totalRevenue / totalCustomers : 0;
  return <div className="space-y-6" data-id="element-4001">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-4002">
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-4003">
          <div className="text-sm font-medium text-gray-500" data-id="element-4004">
            Total Customers
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-4005">
            {totalCustomers}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-4006">
          <div className="text-sm font-medium text-gray-500" data-id="element-4007">
            Active Customers
          </div>
          <div className="mt-2 text-3xl font-semibold text-green-600" data-id="element-4008">
            {activeCustomers}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-4009">
          <div className="text-sm font-medium text-gray-500" data-id="element-4010">Total Revenue</div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-4011">
            Rs. {totalRevenue.toLocaleString('en-LK')}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-4012">
          <div className="text-sm font-medium text-gray-500" data-id="element-4013">
            Avg. Revenue/Customer
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-4014">
            Rs. {Math.round(avgRevenue).toLocaleString('en-LK')}
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-4015">
        {/* Header */}
        <div className="p-6 border-b border-gray-200" data-id="element-4016">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4017">
            <div className="flex-1 max-w-lg" data-id="element-4018">
              <div className="relative" data-id="element-4019">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-4020" />
                <input type="text" placeholder="Search customers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4021" />
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-4022">
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4023">
                <option value="all" data-id="element-4024">All Status</option>
                <option value="active" data-id="element-4025">Active</option>
                <option value="inactive" data-id="element-4026">Inactive</option>
              </select>
              <button onClick={() => navigate('/customer-form')} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-4027">
                <PlusIcon size={20} data-id="element-4028" />
                New Customer
              </button>
            </div>
          </div>
        </div>
        {/* Table */}
        <div className="overflow-x-auto" data-id="element-4029">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-4030">
            <thead className="bg-gray-50" data-id="element-4031">
              <tr data-id="element-4032">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4033">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4034">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4035">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4036">
                  Total Spent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4037">
                  Invoices
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4038">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4039">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-4040">
              {filteredCustomers.map(customer => <tr key={customer.id} className="hover:bg-gray-50" data-id="element-4041">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-4042">
                    <div className="flex items-center" data-id="element-4043">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-medium" data-id="element-4044">
                        {customer.name.charAt(0)}
                      </div>
                      <div className="ml-4" data-id="element-4045">
                        <div className="text-sm font-medium text-gray-900" data-id="element-4046">
                          {customer.name}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-4047">
                          Since{' '}
                          {new Date(customer.createdAt).toLocaleDateString('en-GB')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-4048">
                    <div className="text-sm text-gray-900" data-id="element-4049">
                      <div className="flex items-center gap-1 mb-1" data-id="element-4050">
                        <MailIcon size={14} className="text-gray-400" data-id="element-4051" />
                        {customer.email}
                      </div>
                      {customer.phone && <div className="flex items-center gap-1" data-id="element-4052">
                          <PhoneIcon size={14} className="text-gray-400" data-id="element-4053" />
                          {customer.phone}
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-4054">
                    <div className="text-sm text-gray-900" data-id="element-4055">{customer.city}</div>
                    <div className="text-sm text-gray-500" data-id="element-4056">
                      {customer.country}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-4057">
                    <div className="text-sm font-medium text-gray-900" data-id="element-4058">
                      Rs. {customer.totalSpent.toLocaleString('en-LK')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-4059">
                    <div className="text-sm text-gray-900" data-id="element-4060">
                      {customer.invoiceCount}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-4061">
                    {getStatusBadge(customer.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-4062">
                    <div className="flex items-center justify-end gap-2" data-id="element-4063">
                      <button onClick={() => navigate(`/customer-detail/${customer.id}`)} className="text-blue-600 hover:text-blue-900" title="View" data-id="element-4064">
                        <EyeIcon size={18} data-id="element-4065" />
                      </button>
                      <button onClick={() => navigate(`/customer-form?id=${customer.id}`)} className="text-gray-600 hover:text-gray-900" title="Edit" data-id="element-4066">
                        <EditIcon size={18} data-id="element-4067" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Empty State */}
        {filteredCustomers.length === 0 && <div className="text-center py-12" data-id="element-4068">
            <p className="text-gray-500" data-id="element-4069">No customers found</p>
          </div>}
      </div>
    </div>;
}